// MANAGED-POLICY (build order 2026-10-28, increment 1 B "Managed mode: clients can't add agents").
// A company's `agent_mode` says who adds its agents (DEs and AGs): `managed` (the default for a
// client an agency creates) — its people send a request and the agency's operators fulfil or
// decline it; `self_serve` — its managers add them. An agency admin switches the mode.
//
//   GET  /v1/companies/{co}/agent-mode                    the mode, and whether the caller may switch it
//   PUT  /v1/companies/{co}/agent-mode                    an agency admin switches it
//   GET  /v1/companies/{co}/agent-requests                managers and operators: all; others: their own
//   POST /v1/companies/{co}/agent-requests                ask for a DE (managers) or an AG (members)
//   POST /v1/companies/{co}/agent-requests/{id}/resolve   an operator fulfils or declines it
//   GET  /v1/operator/agent-requests                      the operator's queue across companies
//
// HELPERS (paid helper requests, founder 2026-10-30): kind `helper` asks for a new helper for one
// DE (`de_membership_id`), in any company, managed or not; anyone who may message the DE asks.
// Its `stage` runs requested → quoted → approved → paid → building → added (or declined):
//   POST /v1/companies/{co}/agent-requests/{id}/approve   the requester or a manager approves the quote
//   POST /v1/companies/{co}/agent-requests/{id}/stage     the operator (or the billing side,
//                                                         wp/SKILLSMITH-OPPORTUNITIES) moves it
import { z } from "zod";
import {
  AgentRequestKind,
  AgentRequestState,
  CompanyAgentMode,
  HelperRequestStage,
} from "../enums";
import { Slug, Uuid } from "../primitives";
import { AgentRequest, HelperQuote } from "../rows";
import { CompanyItemParams, CompanyParams } from "./common";
import { defineRoute } from "./route";

const co = "/v1/companies/{co}";
const op = "/v1/operator";
const core = { feature: "core", tag: "agent-requests" } as const;

export const AgentModeView = z
  .object({
    mode: CompanyAgentMode,
    /** The caller is an admin of an agency serving the company: it may switch the mode. */
    can_change: z.boolean(),
    /** The caller may add agents here (self-serve, or an agency operator). */
    can_add: z.boolean(),
  })
  .meta({ id: "AgentModeView" });
export type AgentModeView = z.infer<typeof AgentModeView>;

export const AgentModePut = z.strictObject({ mode: CompanyAgentMode }).meta({ id: "AgentModePut" });
export type AgentModePut = z.infer<typeof AgentModePut>;

/** A request with its requester's name (the list and the operator queue). */
export const AgentRequestView = AgentRequest.extend({
  requested_by_name: z.string(),
}).meta({ id: "AgentRequestView" });
export type AgentRequestView = z.infer<typeof AgentRequestView>;

export const AgentRequestCreate = z
  .strictObject({
    kind: AgentRequestKind,
    role: z.string().trim().min(1).max(200),
    /** A role from the template catalog (`executive-assistant`), when one fits. */
    template_key: z
      .string()
      .max(64)
      .regex(/^[a-z0-9][a-z0-9-]{0,63}$/)
      .nullable()
      .optional(),
    note: z.string().trim().max(2000).nullable().optional(),
    /** HELPERS: the DE a `helper` request is for (required for that kind, refused otherwise). */
    de_membership_id: Uuid.optional(),
  })
  .refine((b) => (b.kind === "helper") === (b.de_membership_id !== undefined), {
    path: ["de_membership_id"],
    message: "a helper request names its DE; other kinds do not",
  })
  .meta({ id: "AgentRequestCreate" });
export type AgentRequestCreate = z.infer<typeof AgentRequestCreate>;

export const AgentRequestResolve = z
  .strictObject({
    outcome: z.enum(["fulfilled", "declined"]),
    note: z.string().trim().max(2000).nullable().optional(),
    /** The DE's membership or the AG the request became (fulfilled only). */
    fulfilled_ref_id: Uuid.nullable().optional(),
  })
  .meta({ id: "AgentRequestResolve" });
export type AgentRequestResolve = z.infer<typeof AgentRequestResolve>;

export const AgentRequestListQuery = z.object({
  state: AgentRequestState.optional(),
  /** HELPERS: one kind only, and (helper requests) one DE's. */
  kind: AgentRequestKind.optional(),
  de_membership_id: Uuid.optional(),
});

/**
 * HELPERS: move a helper request on (the operator, or the billing side). `quoted` carries the
 * quote; `added` may name the helper it became (`fulfilled_ref_id`); `approved` is the client's
 * (`agentRequests.approve`), `requested` is where it starts.
 */
export const HelperRequestStageMove = z
  .strictObject({
    stage: HelperRequestStage.exclude(["requested", "approved"]),
    quote: HelperQuote.optional(),
    note: z.string().trim().max(2000).nullable().optional(),
    fulfilled_ref_id: Uuid.nullable().optional(),
  })
  .refine((b) => b.stage === "quoted" || b.quote === undefined, {
    path: ["quote"],
    message: "a quote goes with stage quoted",
  })
  .meta({ id: "HelperRequestStageMove" });
export type HelperRequestStageMove = z.infer<typeof HelperRequestStageMove>;

/** The newest this many requests are listed (a company asks for a handful). */
export const AGENT_REQUEST_LIST_LIMIT = 200;

export const OperatorAgentRequests = z
  .object({
    data: z.array(
      z.object({
        company: z.object({ id: Uuid, slug: Slug, name: z.string() }),
        request: AgentRequestView,
      }),
    ),
    /** False when a company could not be read; `incomplete_reasons` say how many. */
    complete: z.boolean(),
    incomplete_reasons: z.array(z.string()),
  })
  .meta({ id: "OperatorAgentRequests" });
export type OperatorAgentRequests = z.infer<typeof OperatorAgentRequests>;

export const agentRequestRoutes = {
  "agentMode.get": defineRoute({
    ...core,
    id: "agentMode.get",
    method: "get",
    path: `${co}/agent-mode`,
    summary: "Who adds agents here: the company (self-serve) or its agency (managed)",
    access: "viewer",
    params: CompanyParams,
    response: { status: 200, schema: AgentModeView },
    errors: [],
    emits: [],
  }),
  "agentMode.put": defineRoute({
    ...core,
    id: "agentMode.put",
    method: "put",
    path: `${co}/agent-mode`,
    summary: "Switch the company between managed and self-serve (an agency admin)",
    access: "operator",
    params: CompanyParams,
    body: AgentModePut,
    response: { status: 200, schema: AgentModeView },
    errors: [],
    emits: ["company.agent_mode_changed"],
  }),
  "agentRequests.list": defineRoute({
    ...core,
    id: "agentRequests.list",
    method: "get",
    path: `${co}/agent-requests`,
    summary: "Requests for agents, newest first (managers all, everyone else their own)",
    access: "member",
    params: CompanyParams,
    query: AgentRequestListQuery,
    response: { status: 200, schema: z.object({ data: z.array(AgentRequestView) }) },
    errors: [],
    emits: [],
  }),
  "agentRequests.create": defineRoute({
    ...core,
    id: "agentRequests.create",
    method: "post",
    path: `${co}/agent-requests`,
    summary: "Ask the agency for a digital employee (managers) or an agent (members)",
    access: "member",
    params: CompanyParams,
    body: AgentRequestCreate,
    idempotency: "optional",
    response: { status: 201, schema: AgentRequestView },
    errors: [],
    emits: ["agent_request.created"],
  }),
  "agentRequests.resolve": defineRoute({
    ...core,
    id: "agentRequests.resolve",
    method: "post",
    path: `${co}/agent-requests/{id}/resolve`,
    summary: "Fulfil or decline a request (the agency's operators)",
    access: "operator",
    params: CompanyItemParams,
    body: AgentRequestResolve,
    idempotency: "optional",
    response: { status: 200, schema: AgentRequestView },
    errors: ["conflict"],
    emits: ["agent_request.resolved"],
  }),
  "agentRequests.approve": defineRoute({
    ...core,
    id: "agentRequests.approve",
    method: "post",
    path: `${co}/agent-requests/{id}/approve`,
    summary: "Approve a helper request's quote (the requester or a manager)",
    access: "member",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: AgentRequestView },
    errors: ["conflict"],
    emits: ["agent_request.stage_changed", "helper_offer.changed"],
  }),
  "agentRequests.stage": defineRoute({
    ...core,
    id: "agentRequests.stage",
    method: "post",
    path: `${co}/agent-requests/{id}/stage`,
    summary:
      "Move a helper request on: quoted, paid, building, added or declined (the agency's operators)",
    access: "operator",
    params: CompanyItemParams,
    body: HelperRequestStageMove,
    idempotency: "optional",
    response: { status: 200, schema: AgentRequestView },
    errors: ["conflict"],
    emits: ["agent_request.stage_changed", "agent_request.resolved", "helper_offer.changed"],
  }),
  "operator.agentRequests.list": defineRoute({
    ...core,
    id: "operator.agentRequests.list",
    method: "get",
    path: `${op}/agent-requests`,
    tag: "agency-console",
    summary: "Requests for agents on the operator's companies (open by default)",
    access: "operator",
    query: AgentRequestListQuery,
    response: { status: 200, schema: OperatorAgentRequests },
    errors: [],
    emits: [],
  }),
} as const;
